import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Loader2,
  Search,
  MessageCircle,
  ChevronDown,
  Sparkles,
  Heart,
  Users,
  Shield,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import useWin98Scope from '../hooks/useWin98Scope';

const MANDOMASTER_URL = 'https://mando.kybersuite.com';

// Public, no-login gallery of MandoMaster community builds. Standalone (no authed Layout)
// so logged-out visitors render cleanly — it doubles as a funnel landing for MandoMaster.
const ExplorePage = () => {
  useWin98Scope();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const limit = 12;

  const fetchPosts = useCallback(async (reset = false) => {
    try {
      const currentOffset = reset ? 0 : offset;
      let url = `/community?limit=${limit}&offset=${currentOffset}&source=mandomaster`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const response = await api.get(url);
      if (reset) {
        setPosts(response.data);
        setOffset(limit);
      } else {
        setPosts((prev) => [...prev, ...response.data]);
        setOffset(currentOffset + limit);
      }
      setHasMore(response.data.length === limit);
    } catch (error) {
      console.error('Failed to fetch Mando builds:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [offset, search]);

  useEffect(() => {
    setLoading(true);
    setOffset(0);
    fetchPosts(true);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      setLoadingMore(true);
      fetchPosts(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  // Logged-out users land on login then bounce to the build; logged-in users go straight there.
  const rememberBuild = (id) => {
    try { localStorage.setItem('postLoginRedirect', `/social/${id}`); } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[var(--win-face)] border-b-2 border-[var(--win-shadow)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 font-heading font-bold text-lg">
            <Sparkles className="w-6 h-6" />
            <span>KyberCosplay</span>
          </Link>
          <div className="flex items-center gap-2">
            <a
              href={MANDOMASTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="win-btn hidden sm:inline-flex items-center gap-1.5 text-sm"
            >
              <Shield className="w-4 h-4" /> Build in MandoMaster
            </a>
            {user ? (
              <Link to="/social?tab=mando">
                <Button variant="secondary" className="gap-1.5 font-semibold">
                  Full community <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" className="win-btn inline-flex items-center text-sm">
                  Sign in
                </Link>
                <Link to="/register">
                  <Button className="font-semibold">Join</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Hero */}
        <div className="text-center max-w-2xl mx-auto pt-2 pb-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 win-well text-xs font-semibold uppercase tracking-widest mb-3">
            <Shield className="w-3.5 h-3.5" /> MandoMaster Builds
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold">Get inspired</h1>
          <p className="mt-2">Browse Mandalorian builds shared by the community, then design your own.</p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-3 max-w-md mx-auto">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search builds, characters, series..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
              data-testid="explore-search-input"
            />
          </div>
          <Button type="submit" variant="secondary">Search</Button>
        </form>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="win-well p-12 text-center max-w-lg mx-auto">
            <Shield className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-lg font-heading font-semibold mb-2">No builds yet</h3>
            <p className="mb-6">
              {search ? `No builds found matching "${search}"` : 'Be the first to design and share a Mandalorian!'}
            </p>
            <a href={MANDOMASTER_URL} target="_blank" rel="noopener noreferrer">
              <Button variant="secondary" className="win-btn-default gap-2 font-semibold">
                <Shield className="w-5 h-5" /> Build in MandoMaster
              </Button>
            </a>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/social/${post.id}`}
                  onClick={() => rememberBuild(post.id)}
                  className="group"
                  data-testid={`explore-post-${post.id}`}
                >
                  <div className="glass-card overflow-hidden h-full flex flex-col">
                    {/* Cover Image */}
                    <div className="aspect-[4/5] bg-slate-800/50 relative overflow-hidden">
                      {post.cover_image ? (
                        <img
                          src={post.cover_image}
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Users className="w-16 h-16" />
                        </div>
                      )}

                      <div className="absolute top-3 left-3 px-2 py-1 win-raised text-[10px] font-bold uppercase tracking-wider">
                        Mando
                      </div>

                      {post.comment_count > 0 && (
                        <div className="absolute top-3 right-3 px-2 py-1 win-raised flex items-center gap-1">
                          <MessageCircle className="w-4 h-4" />
                          <span className="text-xs font-medium">{post.comment_count}</span>
                        </div>
                      )}

                      {/* Like count (read-only on the public gallery) */}
                      <div className="absolute bottom-3 right-3 px-2 py-1 win-raised flex items-center gap-1">
                        <Heart className="w-4 h-4" />
                        <span className="text-xs font-medium">{post.like_count || 0}</span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 flex-1 flex flex-col">
                      <h3 className="font-heading font-semibold text-lg truncate">
                        {post.title}
                      </h3>
                      <p className="text-sm truncate">
                        {post.character_name}
                        {post.series_name && ` • ${post.series_name}`}
                      </p>

                      <div className="flex items-center gap-2 mt-auto pt-3 border-t border-[var(--win-shadow)]">
                        {post.user_profile_picture ? (
                          <img src={post.user_profile_picture} alt={post.user_name} className="w-6 h-6 object-cover" />
                        ) : (
                          <div className="w-6 h-6 flex items-center justify-center bg-[var(--win-titlebar-start)]">
                            <span className="text-xs text-white font-bold">{post.user_name?.charAt(0)?.toUpperCase() || '?'}</span>
                          </div>
                        )}
                        <span className="text-sm truncate flex-1">{post.user_name}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center pt-2">
                <Button
                  onClick={loadMore}
                  disabled={loadingMore}
                  variant="secondary"
                  className="gap-2"
                  data-testid="explore-load-more-btn"
                >
                  {loadingMore ? <Loader2 className="w-5 h-5 animate-spin" /> : (<><ChevronDown className="w-5 h-5" /> Load More</>)}
                </Button>
              </div>
            )}
          </>
        )}

        {/* Footer CTA */}
        <div className="glass-card p-6 sm:p-8 text-center mt-4">
          <h2 className="text-2xl font-heading font-bold">Ready to build your own?</h2>
          <p className="mt-1 mb-5">Design custom Mandalorian armor in MandoMaster — it imports straight into KyberCosplay.</p>
          <a href={MANDOMASTER_URL} target="_blank" rel="noopener noreferrer">
            <Button className="font-semibold gap-2 px-6">
              <Shield className="w-5 h-5" /> Open MandoMaster
            </Button>
          </a>
        </div>
      </main>
    </div>
  );
};

export default ExplorePage;
